import { useCallback, useEffect, useRef, useState } from 'react';
import { VideoSurface } from './VideoSurface';

interface Props {
  stream: MediaStream | null;
}

interface Offset {
  x: number;
  y: number;
}

/**
 * Локальне прев'ю, яке можна перетягнути.
 *
 * Тримається за нижній правий кут і рухається зміщенням, а не абсолютними
 * координатами — тож при зміні розміру вікна або повороті екрана не тікає
 * за межі видимого.
 *
 * ⚠️ Реалізація навмисно НЕ покладається на `setPointerCapture`: у WebKit
 * захоплення вказівника на елементі, який перемальовується на кожен рух,
 * губить наступні `pointermove`. Тому слухачі руху й відпускання вішаються
 * на `window` на час перетягування — цей шлях однаково працює і в Chrome,
 * і в Safari, і на дотику, і мишею.
 */
export function DraggablePreview({ stream }: Props): React.JSX.Element {
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const nodeRef = useRef<HTMLDivElement>(null);

  const clamp = useCallback((next: Offset): Offset => {
    const node = nodeRef.current;
    if (!node) return next;
    const rect = node.getBoundingClientRect();
    // Межі рахуємо від початкового кута: зміщення вліво і вгору — відʼємні.
    const maxLeft = window.innerWidth - rect.width - 14;
    const maxUp = window.innerHeight - rect.height - 14;
    return {
      x: Math.min(0, Math.max(-maxLeft, next.x)),
      y: Math.min(0, Math.max(-maxUp, next.y)),
    };
  }, []);

  useEffect(() => {
    // Після повороту екрана прев'ю могло опинитися за межами — підтягуємо.
    const onResize = (): void => {
      setOffset((current) => clamp(current));
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [clamp]);

  // Слухачі вішаються ПРЯМО в обробнику натискання, а не через useEffect.
  // Ефект спрацював би вже після ререндера, і швидкий жест устигав би
  // надіслати перші pointermove у порожнечу — перетягування «не працювало б»
  // саме на різких рухах.
  const stopRef = useRef<(() => void) | null>(null);

  const beginDrag = useCallback(
    (startX: number, startY: number, base: Offset): void => {
      stopRef.current?.();

      const onMove = (event: PointerEvent): void => {
        // Без цього WebKit тягне сторінку разом із прев'ю.
        if (event.cancelable) event.preventDefault();
        setOffset(
          clamp({
            x: base.x + (event.clientX - startX),
            y: base.y + (event.clientY - startY),
          }),
        );
      };

      const onEnd = (): void => {
        stopRef.current?.();
      };

      // passive: false — інакше preventDefault ігнорується на дотику.
      window.addEventListener('pointermove', onMove, { passive: false });
      window.addEventListener('pointerup', onEnd);
      window.addEventListener('pointercancel', onEnd);

      stopRef.current = (): void => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onEnd);
        window.removeEventListener('pointercancel', onEnd);
        stopRef.current = null;
        setDragging(false);
      };

      setDragging(true);
    },
    [clamp],
  );

  // Розмонтування посеред жесту не має лишати слухачів на window.
  useEffect(() => () => stopRef.current?.(), []);

  return (
    <div
      ref={nodeRef}
      className={dragging ? 'stage__local stage__local--dragging' : 'stage__local'}
      style={{ transform: `translate(${String(offset.x)}px, ${String(offset.y)}px)` }}
      onPointerDown={(event) => {
        beginDrag(event.clientX, event.clientY, offset);
      }}
      // Клік по прев'ю не має ховати панель керування під ним.
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <VideoSurface stream={stream} muted mirrored />
    </div>
  );
}
